import { format } from 'date-fns'
import type { TweDailyClicks } from 'api/generated'
import { axisProps, formatRowDate } from 'helpers/daily-report'
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'

type Point = TweDailyClicks[number]

const DayTooltip = ({
  active,
  payload,
}: {
  active?: boolean
  payload?: ReadonlyArray<{ payload?: unknown }>
}) => {
  const point = payload?.[0]?.payload as Point | undefined
  if (!active || !point) return null
  return (
    <div className='rounded-md border bg-popover px-3 py-2 text-xs text-popover-foreground shadow-sm'>
      <div className='text-muted-foreground'>
        {formatRowDate(point.date, 'day')}
      </div>
      <div className='font-semibold tabular-nums'>
        {point.clicks_count.toLocaleString('it-IT')} click
      </div>
    </div>
  )
}

/**
 * TWE clicks per day, styled like the dashboard's MetricTrendChart bars.
 * The API sends every day of the window, empty days at 0.
 */
export const TweDailyClicksChart = ({
  daily,
  height = 220,
}: {
  daily: TweDailyClicks
  height?: number
}) => (
  <div style={{ height }}>
    <ResponsiveContainer width='100%' height='100%'>
      <BarChart data={daily} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
        <CartesianGrid
          vertical={false}
          stroke='var(--border)'
          strokeDasharray='3 3'
        />
        <XAxis
          dataKey='date'
          tickFormatter={(d: string) =>
            format(new Date(`${d}T00:00:00`), 'dd/MM')
          }
          minTickGap={24}
          {...axisProps}
        />
        <YAxis
          allowDecimals={false}
          tickFormatter={(v: number) => v.toLocaleString('it-IT')}
          width={48}
          domain={[0, 'auto']}
          {...axisProps}
        />
        <Tooltip
          cursor={{ fill: 'var(--muted)' }}
          content={({ active, payload }) => (
            <DayTooltip active={active} payload={payload} />
          )}
        />
        <Bar
          dataKey='clicks_count'
          fill='var(--primary)'
          radius={[4, 4, 0, 0]}
          maxBarSize={32}
          isAnimationActive={false}
        />
      </BarChart>
    </ResponsiveContainer>
  </div>
)
